/* eslint-disable import/order -- the `react-semantic-ui-datepickers` stylesheet is a
   SIDE-EFFECT import, and the ordering this rule asks for moves module imports across
   it. Stylesheet order is cascade order, so that is a change to what the page renders,
   not to its types. */
import React, { useState, useEffect } from 'react';
import {
  Message,
  Comment,
  Dimmer,
  Loader,
  Dropdown,
  Button,
  Popup,
  Segment,
  Divider,
  Confirm,
  Form,
  Label,
  Icon,
} from 'semantic-ui-react';
import type { DropdownItemProps } from 'semantic-ui-react';

import 'react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css';
import type { FlatNote, NoteThread, Option, PractitionerOption } from 'types';

import { useAuthContext } from '../../../contexts/AuthContext';

import * as fhirpath from 'fhirpath';

import noteTypes from '../../../data/NoteTypes';
import { FHIRProxyClient, ProvenanceBuilder } from '../../../fhir-sdoh';

import type { DocumentReference, Flag, Provenance } from 'fhir/r4';
import moment from 'moment';

import * as FHIRUtils from '../../../utils/FHIRUtils';
import * as Helpers from '../../../utils/Helpers';
import type { ResourceEntry } from '../../../utils/bundleNarrowing';
import { downloadNotesPdf } from '../../../utils/pdfDownload';
import type { PdfNoteItem } from '../../../utils/pdfDownload';

import AddChild from './addChild';
import { fetchDocumentReference } from './documentReferenceQueries';
import CreateNote from './new';

// For display of all the Notes (All categories)
export type NotesListAllProps = {
  // Never absent: notes/index.tsx holds it as `useState<unknown[]>([])` and the
  // `onSuccess` handler only ever stores an array, so every read here (lines
  // 400, 555, 833) that dereferences it without a guard is safe today.
  notesResponse: ResourceEntry<'DocumentReference'>[];
  org: string;
  patientId: string;
  practitionerData: ResourceEntry<'Practitioner'>[] | undefined;
  refresh: () => void;
  // notes/tabs.tsx renders this component only for the 'ALL' tab.
  type: 'ALL';
  // Only `id` and `referralId` are read here (the Edit-Referral-Id dropdown), so
  // this states the structural minimum this component requires rather than naming
  // a concrete referral type. Both fields carry `| undefined` explicitly: the
  // producer's `ReferralSummary` declares them optional, and under
  // `exactOptionalPropertyTypes` a bare `?:` here would not accept that.
  allReferralData?: { id?: string | undefined; referralId?: string | undefined }[] | undefined;
  // Supplied by notes/tabs.tsx but never read in this component.
  setNotesResponse?: unknown;
};

function NotesListAll(props: NotesListAllProps) {
  const authInfo = useAuthContext();
  const practitionerName = authInfo.practitionerName;

  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [tableData, setTableData] = useState<Record<string, NoteThread>>({});
  const [allTableData, setAllTableData] = useState<Record<string, NoteThread>>({});
  const [sortTableData, setSortTableData] = useState(false);
  const noteCategories = {
    FEEDBACK: { code: '34109-9', display: 'Evaluation and management note' },
    ACTIONABLE_INSIGHTS: { code: '29751-5', display: 'Critical care records' },
    GENERAL: { code: '56447-6', display: 'Plan of care note' },
    ALL: { code: '56447-6', display: 'Plan of care note' },
  };
  const [collapsed, setCollapsed] = useState(false);
  const [loaderActive, setLoaderActive] = useState(false);
  const [replyTextList, setReplyTextList] = useState({});
  const [filterNoteTypes, setFilterNoteTypes] = useState<Option[]>([]);
  const [_filterNoteTypesAlias, _setFilterNoteTypesAlias] = useState({});
  const [allPractitioners, setAllPractitioners] = useState<PractitionerOption[]>([]);
  // Each is declared as the union its own setter calls already pass — `setOpen`
  // receives a note key (line 686) and `null` (357, 723); `setSelectedReferralId` receives a
  // `NoteThread['referralId']` (645) and `[]` (585); `setSelectedReferralNoteId`
  // receives a `FlatNote['id']` (646) and `null` (587, 631).
  const [open, setOpen] = useState<string | null | undefined>();
  const [openReferralDropdown, setOpenReferralDropdown] = useState(false);
  // The elements are FHIR `Identifier.value`s (written back at line 550) and
  // semantic-ui Dropdown values, both of which are strings.
  const [selectedReferralId, setSelectedReferralId] = useState<string[]>([]);
  const [selectedReferralNoteId, setSelectedReferralNoteId] = useState<string | null>(null);

  const system = 'http://loinc.org';

  const sortedChildNotes: Record<string, FlatNote[]> = {};
  // let allNoteType = [{ text: "ALL", key: "ALL", value: "ALL" }]

  // useEffect(() => {
  //   if (props.type === "FEEDBACK") {
  //     // eslint-disable-next-line react-hooks/exhaustive-deps
  //     system = "https://projectwell.io/fhir/codes/feedback-type";
  //   } else if (props.type === "ACTIONABLE_INSIGHTS") {
  //     system = "https://projectwell.io/fhir/codes/ai-type";
  //   }

  //   if(props.type!=="TASKS_NOTES"){
  //     // allNoteType.concat(noteTypes[props.type]);
  //     let alias = {}
  //     noteTypes.ALL_FILTER.forEach(element => {
  //       alias[element.text] = element.alias;
  //     });
  //     setFilterNoteTypesAlias(alias);
  //     setFilterNoteTypes(allNoteType.concat(noteTypes["ALL_FILTER"]));
  //   }

  // }, [props.type]);

  useEffect(() => {
    // reset notes data on new data response
    setTableData({});
    setAllTableData({});
    if (props.notesResponse && props.notesResponse.length) {
      establishParentChildRelationship(props.notesResponse);
    }
     
  }, [props.notesResponse, props.type]);

  useEffect(() => {
    if (props.practitionerData && props.practitionerData) {
      const practitionersList = props.practitionerData.map((item) => {
        const practitioner = FHIRUtils.parseFHIRPractitioner(item.resource);
        const isMale = practitioner.gender.toLowerCase() === 'male' ? true : false;
        return {
          key: practitioner.id,
          text: practitioner.fullName,
          value: practitioner.id,
          image: { avatar: true, src: '/images/' + Helpers.getImage(isMale, practitioner?.fullName) },
        };
      });
      setAllPractitioners(practitionersList);
    }
  }, [props.practitionerData]);

  useEffect(() => {
    setValidationErrors([]);
  }, []);

  const toggleCollapse = (value: boolean) => {
    return !value;
  };

  const parentChildRelationships = (
    resource: DocumentReference,
    fhirNote: FlatNote,
    notesList: Record<string, NoteThread>,
    replyNoteIds: Record<string, boolean>
  ) => {
    fhirNote.date = fhirNote.date && moment(fhirNote.date, 'YYYY-MM-DD').format('MMMM Do, YYYY');

    const referenceStr = fhirpath.evaluate(resource, 'relatesTo.first().target.reference').toString();

    const reference = referenceStr && referenceStr.split('/')[1];
    if (reference !== '' && reference !== undefined) {
      const parsedChildNote = FHIRUtils.parseFHIRNotes(resource);
      replyNoteIds[parsedChildNote.id] = false;
      if (Object.prototype.hasOwnProperty.call(notesList, reference)) {
        // The `hasOwnProperty` test above is what makes this read safe at
        // runtime; noUncheckedIndexedAccess cannot see through it.
        (notesList[reference] as NoteThread).child.push(parsedChildNote);
        setReplyTextList((prevState) => ({
          ...prevState,
          [reference]: '',
        }));
      } else {
        notesList[reference] = { parent: {}, child: [] };
        notesList[reference].child.push(parsedChildNote);
        setReplyTextList((prevState) => ({
          ...prevState,
          [reference]: '',
        }));
      }
    } else {
      // `DocumentReference.id` is `0..1`, and it is used as a computed key
      // unguarded, so an absent id produces the literal key "undefined"; the
      // assertion preserves that rather than filtering it out.
      const resourceId = resource.id as string;
      replyNoteIds[resourceId] = false;
      if (Object.prototype.hasOwnProperty.call(notesList, resourceId)) {
        // Guarded by `hasOwnProperty` above, so the spread source is present and
        // still carries `parent`/`child`; the assertion keeps those required.
        notesList[resourceId] = {
          ...(notesList[resourceId] as NoteThread),
          ...FHIRUtils.parseFHIRNotes(resource),
        };
        setReplyTextList((prevState) => ({
          ...prevState,
          [resourceId]: '',
        }));
      } else {
        notesList[resourceId] = { parent: {}, child: [] };
        notesList[resourceId] = {
          ...notesList[resourceId],
          ...FHIRUtils.parseFHIRNotes(resource),
        };
        setReplyTextList((prevState) => ({
          ...prevState,
          [resourceId]: '',
        }));
      }
    }
  };

  const establishParentChildRelationship = (notesData: ResourceEntry<'DocumentReference'>[]) => {
    const notesList: Record<string, NoteThread> = {};
    const replyNoteIds: Record<string, boolean> = {};

    const filter = [{ text: 'All', key: 'ALL', value: 'ALL' }];

    notesData.forEach((note) => {
      const resource = note.resource;
      const fhirNote = FHIRUtils.parseFHIRNotes(resource);

      parentChildRelationships(resource, fhirNote, notesList, replyNoteIds);
      if (fhirNote.code !== '48767-8')
        // Ignoring child notes for filter
        filter.push({
          text: fhirNote.type,
          key: fhirNote.code,
          value: fhirNote.code,
        });
    });

    // Remove duplicates from Array of objects
    const uniqueFilter = filter.filter((v, i, a) => a.findIndex((v2) => JSON.stringify(v2) === JSON.stringify(v)) === i);
    setFilterNoteTypes(uniqueFilter);

    for (const noteThread in notesList) {
      // `for...in` only yields keys that exist, so this entry is present.
      const child = (notesList[noteThread] as NoteThread).child;

      child.sort(function (a, b) {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });
      (notesList[noteThread] as NoteThread).child = child;
    }

    setTableData((prevState) => ({
      ...prevState,
      ...notesList,
    }));

    setAllTableData((prevState) => ({
      ...prevState,
      ...notesList,
    }));
  };

  //FHIR Call to create a Child Note
  const createNote = (
    type: string,
    notePayload: DocumentReference,
    parentId: string,
    flagpayload: Flag | undefined = undefined,
    additionalflagpayload: Flag | undefined = undefined
  ) => {
    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

    //create Provenance
    const provenance: Provenance = ProvenanceBuilder.build(
      `DocumentReference/${notePayload.id!}`,
      {
        reference: 'Practitioner/' + authInfo.practitionerId,
        display: practitionerName,
      },
      props.patientId,
      'CREATE',
      `A Child note (${notePayload.description}) has been created to ${type} note`,
      { code: props.org.toUpperCase(), display: props.org.toLowerCase() } //eg. {Organization/HUMANA,humana}
    );

    //now post bundle request
    let fhirBundle;
    if (flagpayload) {
      if (additionalflagpayload) {
        fhirBundle = ProvenanceBuilder.buildProvenanceBundle(
          [notePayload, flagpayload, additionalflagpayload],
          provenance
        );
      } else {
        fhirBundle = ProvenanceBuilder.buildProvenanceBundle([notePayload, flagpayload], provenance);
      }
    } else {
      if (additionalflagpayload) {
        fhirBundle = ProvenanceBuilder.buildProvenanceBundle([notePayload, additionalflagpayload], provenance);
      } else {
        fhirBundle = ProvenanceBuilder.buildProvenanceBundle([notePayload], provenance);
      }
    }
    setLoaderActive(true);

    /* eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- the value is a `fhir-sdoh` export whose module ships no usable types */
    fhirProxyClient.send('post', '', fhirBundle).then(
      (_response) => {
        setLoaderActive(false);
        //(clear reply text id after adding child)
        setReplyTextList((prevState) => ({
          ...prevState,
          [parentId]: '',
        }));
        // calling mutate
        props.refresh();
      },
      (error: { toString(): string }) => {
        setLoaderActive(false);
        setValidationErrors((state) => [...state, 'Unable to create note: ' + error.toString()]);
      }
    );
  };

  const handleDownloadAllNotes = () => {
    const notesForPdf: PdfNoteItem[] = [];

    sortedParentNotes.forEach((key) => {
      const parentNote = tableData[key];
      if (!parentNote) return;

      notesForPdf.push({
        // `author` is `unknown[]`; the `||` keeps the array when it is non-empty,
        // and interpolating it comma-joins the names.
        heading: `${parentNote.type || 'Note'} - ${(parentNote.author || '') as unknown as string}`,
        body: parentNote.description || '',
        meta: `${moment(parentNote.date).format('MM-DD-YYYY')}${
          parentNote?.referralId?.length ? ' | Referral Id(s): ' + parentNote.referralId.join(', ') : ''
        }`,
      });

      const childNotes = sortedChildNotes[key] || [];
      childNotes.forEach((child) => {
        notesForPdf.push({
          heading: `Reply - ${(child.author || '') as unknown as string}`,
          body: child.description || '',
          meta: `${moment(child.date).format('MM-DD-YYYY')}`,
        });
      });
    });

    downloadNotesPdf({
      title: `${props.type || 'Notes'} Notes`,
      patientId: props.patientId,
      notes: notesForPdf,
      fileName: `${(props.type || 'notes').replace(/[^a-z0-9]/gi, '_')}_notes_${props.patientId}.pdf`,
    });

    logNotesDownload(`General notes have been downloaded`);
  };

  const logNotesDownload = (message: string) => {
    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

    const provenance: Provenance = ProvenanceBuilder.build(
      `Patient/${props.patientId}`,
      { reference: 'Practitioner/' + authInfo.practitionerId, display: practitionerName },
      props.patientId,
      'General Task Notes Download',
      message,
      { code: props.org.toUpperCase(), display: props.org.toLowerCase() }
    );

    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([], provenance);

    fhirProxyClient.send('post', '', fhirBundle).then(
      () => {
        console.log('Notes download provenance logged');
      },
      (error: { toString(): string }) => {
        setValidationErrors((state) => [...state, 'Unable to create provenance: ' + error.toString()]);
      }
    );
  };

  // Populate validation/error messages on the form
  const setValidationMessages = () => {
    return validationErrors.map((error) => {
      return <Message.Item key={error}>{error}</Message.Item>;
    });
  };

  const removeNote = async (noteId: string) => {
    setOpen(null);
    setValidationErrors([]);

    // `?.length` is `number | undefined`; `undefined > 0` is `false`, which is
    // the branch an absent note takes. The assertion states that rather than
    // adding a `?? 0` that would read as new handling.
    if ((tableData[noteId]?.child?.length as number) > 0) {
      setValidationErrors((state) => [
        ...state,
        'Unable to remove note: note has replies. Please remove replies first!',
      ]);
      return;
    }

    if (!props.patientId || !authInfo?.token) {
      setValidationErrors((state) => [...state, 'Unable to remove note: missing patient context.']);
      return;
    }

    const listEntry = props.notesResponse.find((item) => item.resource.id === noteId);
    if (!listEntry?.resource?.id) {
      setValidationErrors((state) => [...state, 'Unable to remove note: note not found.']);
      return;
    }

    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

    setLoaderActive(true);

    try {
      const notePayload: fhir4.DocumentReference = await fetchDocumentReference(
        authInfo.token,
        noteId,
        props.patientId
      );
      notePayload.status = 'entered-in-error';

      let parentNoteType = '';
      // `relatesTo` is `0..*`, so `findIndex` is `number | undefined` here.
      // `undefined > -1` is `false`, i.e. an absent `relatesTo` skips this
      // block; the assertion preserves that without changing the test.
      const index = notePayload?.relatesTo?.findIndex((obj) => obj.code === 'appends') as number;
      if (index > -1) {
        const parentNoteId = notePayload?.relatesTo?.[index]?.target?.reference?.split('/')?.[1];
        // Both the key and the looked-up thread's `type` can be absent; the
        // resulting `undefined` is assigned straight into `parentNoteType` and
        // passed on, and the assertion keeps that value.
        parentNoteType = tableData[parentNoteId as string]?.type as string;
      }

      await fhirProxyClient.send('put', `DocumentReference/${notePayload.id}`, notePayload);

      const provenance: Provenance = ProvenanceBuilder.build(
        `DocumentReference/${notePayload.id}`,
        {
          reference: 'Practitioner/' + authInfo.practitionerId,
          display: practitionerName,
        },
        props.patientId,
        'UPDATE',
        `A Child note (${notePayload.description}) has been moved from ${parentNoteType} note`,
        {
          code: props.org.toUpperCase(),
          display: props.org.toLowerCase(),
        }
      );
      const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([], provenance);

      await fhirProxyClient.send('post', '', fhirBundle);
      props.refresh();
    } catch (error) {
      setValidationErrors((state) => [...state, 'Unable to remove note: ' + String(error)]);
    } finally {
      setLoaderActive(false);
    }
  };

  const displayChild = (child: FlatNote) => {
    return (
      <div key={child.id}>
        <Comment.Group collapsed={collapsed} size="large">
          <Comment>
            {/* `CommentAvatarProps.src` is required `string`. A practitioner absent
                from the list makes `findIndex` return -1, so the subscript is
                `undefined` and the avatar renders with no image — which is what it
                does today. Same at the second avatar in this file. */}
            <Comment.Avatar
              as="a"
              src={
                allPractitioners[
                  allPractitioners.findIndex((practitioner) => practitioner.text === child.author?.[0])
                ]?.image?.src as string
              }
            />
            <Comment.Content>
              <Comment.Author style={{ color: 'black' }} as="a">
                <div className="noteattr_display_text">
                  {child.author}. {moment(child.date).format('MM-DD-YYYY')}
                </div>
              </Comment.Author>
              <Comment.Metadata>
                <Label Label as="a" basic color="red" onClick={() => setOpen(child.id)}>
                  <Icon name="trash" /> Delete
                </Label>
              </Comment.Metadata>
              <Comment.Actions>
                <Comment.Text>{child.description}</Comment.Text>
              </Comment.Actions>
            </Comment.Content>
          </Comment>
        </Comment.Group>
      </div>
    );
  };

  const displayReplyText = (key: string, type: string, code: string) => {
    return (
      <AddChild
        patientId={props.patientId}
        parentId={key}
        createNote={createNote}
        type={type}
        notesType={props.type}
        code={code}
        replyTextList={replyTextList}
        setReplyTextList={setReplyTextList}
        noteCategories={noteCategories}
        org={props.org}
        practitionerData={allPractitioners}
        childNote={sortedChildNotes}
        tableData={tableData}
      />
    );
  };

  const filterNotes = (e: React.MouseEvent<HTMLDivElement>) => {
    // `e.target` is typed `EventTarget`; this handler is bound to a Dropdown.Menu
    // whose click target is always a DOM node, so `textContent` is read off it
    // directly.
    const clickedText = (e.target as Node).textContent;
    const filteredTableData: Record<string, NoteThread> = {};
    if (clickedText === 'All' || clickedText === '') {
      setTableData(allTableData);
    } else {
      for (const key in allTableData) {
        // `for...in` only yields keys that exist, so this entry is present.
        const entry = allTableData[key] as NoteThread;
        if (entry.type === clickedText) {
          filteredTableData[key] = entry;
        }
      }
      setTableData(filteredTableData);
    }
  };

  const sortNotes = (_e: React.MouseEvent<HTMLButtonElement>) => {
    setSortTableData(!sortTableData);
  };

  // Receives the semantic-ui Dropdown's `value` for a `multiple` selection,
  // whose options are built from `allReferralData[].referralId` (strings). The
  // call site asserts, because `DropdownProps['value']` is a single wide union
  // that cannot express "array when `multiple` is set".
  const handleNoteReferralChange = (value: string[]) => {
    setSelectedReferralId(value);
  };

  //this will update the referral ids on both the parent and child note
  const saveReferralUpdaetsOnNote = async (noteId: string) => {
    setLoaderActive(true);

    if (!props.patientId || !authInfo?.token) {
      setLoaderActive(false);
      setValidationErrors((state) => [...state, 'Unable to update note: missing patient context.']);
      return;
    }

    const listEntry = props.notesResponse.find((item) => item.resource.id === noteId);
    if (!listEntry?.resource?.id) {
      setLoaderActive(false);
      setValidationErrors((state) => [...state, 'Unable to update note: note not found.']);
      return;
    }

    const applyReferralIdentifiers = (resource: fhir4.DocumentReference): fhir4.DocumentReference => {
      const identifiers =
        resource.identifier?.filter(
          (identifier) => identifier?.system !== 'https://projectwell.io/fhir/identifiers/referral-id'
        ) ?? [];

      if (selectedReferralId?.length > 0) {
        for (let i = 0; i < selectedReferralId.length; i++) {
          identifiers.push({
            system: 'https://projectwell.io/fhir/identifiers/referral-id',
            value: selectedReferralId[i],
          });
        }
      }

      return { ...resource, identifier: identifiers };
    };

    try {
      const childNotes = sortedChildNotes?.[noteId] ?? [];
      const childIds = childNotes.map((child) => child.id).filter(Boolean);

      const [notePayload, ...childResources] = await Promise.all([
        fetchDocumentReference(authInfo.token, noteId, props.patientId),
        ...childIds.map((childId) => fetchDocumentReference(authInfo.token, childId, props.patientId)),
      ]);

      const updatedNotes = [
        applyReferralIdentifiers(notePayload),
        ...childResources.map(applyReferralIdentifiers),
      ];

      const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
      const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

      const provenance: Provenance = ProvenanceBuilder.build(
        `DocumentReference/${notePayload.id!}`,
        {
          reference: 'Practitioner/' + authInfo.practitionerId,
          display: practitionerName,
        },
        props.patientId,
        'UPDATE',
        `ReferralId has been updated to ${selectedReferralId.toString()} for(${notePayload.description}) note`,
        { code: props.org.toUpperCase(), display: props.org.toLowerCase() }
      );

      const fhirBundle = ProvenanceBuilder.buildProvenanceBundle(updatedNotes, provenance);

      await fhirProxyClient.send('post', '', fhirBundle);

      setSelectedReferralId([]);
      setOpenReferralDropdown(false);
      setSelectedReferralNoteId(null);
      props.refresh();
    } catch (error) {
      setValidationErrors((state) => [...state, 'Unable to update note: ' + String(error)]);
    } finally {
      setLoaderActive(false);
    }
  };

  const displayNoteThread = (key: string, tableData: Record<string, NoteThread>) => {
    // The reads below that carry no `?.` dereference `tableData[key]` unguarded;
    // the assertion preserves that, and the optional-chained reads keep their `?.`.
    const thread = tableData[key] as NoteThread;
    return (
      <Comment key={key} size="large">
        <Comment.Avatar
          as="a"
          src={
            allPractitioners[
              allPractitioners.findIndex((practitioner) => practitioner.text.trim() === thread.author?.[0])
            ]?.image?.src as string
          }
        />
        <Comment.Content>
          <Comment.Author as="a">
            <span className="note_type_text">{thread.type}</span> added by {thread.author}.{' '}
            {moment(thread.date).format('MM-DD-YYYY')}{' '}
          </Comment.Author>
          <Comment.Metadata>
            {tableData[key]?.referralId?.map((referral) => (
              <Label Label as="a" basic color="orange">
                {referral}{' '}
              </Label>
            ))}

            <Popup
              wide="very"
              on="click"
              position="top right"
              closeOnEscape
              hideOnScroll
              closeOnDocumentClick
              onClose={() => {
                setOpenReferralDropdown(false);
                setSelectedReferralNoteId(null);
              }}
              onOpen={() => {
                setOpenReferralDropdown(true);
              }}
              open={openReferralDropdown && thread.id === selectedReferralNoteId}
              trigger={
                <Label
                  Label
                  as="a"
                  basic
                  color="blue"
                  onClick={() => {
                    setOpenReferralDropdown(true);
                    // Both reads can be `undefined` (an absent thread, or a
                    // thread seeded from a reply that has no `referralId`/`id`
                    // yet). That `undefined` is stored in state as-is, and the
                    // assertions keep the stored value identical.
                    setSelectedReferralId(tableData[key]?.referralId as string[]);
                    setSelectedReferralNoteId(thread.id as string);
                  }}
                >
                  <Icon name="edit" /> Edit Referral Id
                </Label>
              }
              content={
                <>
                  <Form.Group widths="equal">
                    <Dropdown
                      basic
                      labeled
                      selection
                      search
                      placeholder="Edit Referral ID"
                      autoComplete="off"
                      selectOnBlur={false}
                      name="referralId"
                      options={
                        // SUSPECTED DEFECT (this file, the `options` prop below):
                        // when `allReferralData` is empty or absent, the `&&`
                        // evaluates to `false` (or `undefined`), and that value —
                        // not an array — is what `Dropdown.options` receives.
                        // `undefined > 0` is likewise `false`, so an absent prop
                        // already took this path. REPORTED, NOT FIXED: replacing
                        // it with `[]` would change what semantic-ui renders.
                        // The assertions state the current value and shape.
                        ((props?.allReferralData?.length as number) > 0 &&
                          props?.allReferralData?.map((k) => {
                            return { key: k?.id, value: k?.referralId, text: k?.referralId?.toUpperCase() };
                          })) as DropdownItemProps[]
                      }
                      multiple
                      value={selectedReferralId}
                      onChange={(e, { value }) => handleNoteReferralChange(value as string[])}
                    ></Dropdown>
                    <Button
                      size="small"
                      content="Save"
                      positive
                      // `FlatNote.id` is optional; a note without one is saved
                      // against `/Observation/undefined`.
                      /* eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain -- the `?.` and the `!` guard DIFFERENT values here — the chain guards the container, the `!` an inner field FHIR leaves optional. Both are upstream's */
                      onClick={() => saveReferralUpdaetsOnNote(tableData[key]?.id!)}
                      loading={loaderActive}
                    />
                  </Form.Group>
                </>
              }
            />

            <Label Label as="a" basic color="red" onClick={() => setOpen(key)}>
              <Icon name="trash" /> Delete
            </Label>
          </Comment.Metadata>
          <Comment.Actions>
            <Comment.Text>
              <div className="noteattr_display_text">{thread.description}</div>
              {(sortedChildNotes[key] as FlatNote[]).map((child) => displayChild(child))}
              {/* `FlatNote.type` and `.code` are both optional; the reply control
                  keys its new-note payload on them, so a thread missing either posts
                  a reply carrying `undefined` in that field. */}
              {displayReplyText(key, thread.type!, thread.code!)}
            </Comment.Text>
          </Comment.Actions>
        </Comment.Content>
      </Comment>
    );
  };

  const sortedParentNotes = Object.keys(tableData).sort(function (a, b) {
    // `tableData[x]` is `NoteThread | undefined` under noUncheckedIndexedAccess,
    // and `date` is itself optional on a thread seeded from a reply. Both are
    // read unguarded — `new Date(undefined)` is Invalid Date, so `getTime()`
    // is NaN and the comparison keeps the incoming order.
    const dateOf = (thread: NoteThread | undefined) => new Date((thread as NoteThread).date as string).getTime();
    if (!sortTableData) {
      return dateOf(tableData[b]) - dateOf(tableData[a]);
    } else {
      return dateOf(tableData[a]) - dateOf(tableData[b]);
    }
  });

  sortedParentNotes.map((parentKey) => (sortedChildNotes[parentKey] = (tableData[parentKey] as NoteThread).child));

  return (
    <div>
      <Dimmer active={loaderActive} inverted>
        <Loader></Loader>
      </Dimmer>

      {/*
        SUSPECTED DEFECT (apps/care-betty/src/components/patient/notes/listAll.tsx:737):
        `open` holds the *key* of the note pending deletion (set at line 692 and
        460), but `Confirm.open` is declared `boolean`. semantic-ui reads it for
        truthiness, so the dialog does open — the mismatch is latent, not visible.
        This TS2375 is pre-existing (it is in the typecheck baseline) and is left
        exactly as it was; `removeNote(open)` genuinely needs the key, so the two
        uses of `open` disagree about its type and only a behaviour change could
        reconcile them. REPORTED, NOT FIXED.
      */}
      {/* `open` holds the id of the note pending deletion (or `null`/`undefined`);
          `Confirm.open` is a boolean, so the id is read as truthiness. */}
      <Confirm
        open={open as unknown as boolean}
        onCancel={() => setOpen(null)}
        onConfirm={() => removeNote(open as string)}
      />

      {validationErrors.length > 0 && (
        <Message error>
          <Message.Header>Error:</Message.Header>
          <Message.List>{setValidationMessages()}</Message.List>
        </Message>
      )}

      <div>
        <CreateNote
          practitionerData={allPractitioners}
          setValidationErrors={setValidationErrors}
          patientId={props.patientId}
          token={authInfo.token}
          type={props.type}
          refresh={props.refresh}
          noteTypes={noteTypes[props.type]}
          system={system}
          noteCategories={noteCategories}
          org={props.org}
          allReferralData={props?.allReferralData}
        />
      </div>
      <Segment>
        {filterNoteTypes.length > 0 && (
          <>
            {' '}
            <div className="cb_height_30">
              <span className="cb_floatleft">
                <Popup
                  content="Sort"
                  trigger={<Button circular onClick={sortNotes} name="all" icon="sort" color="teal" />}
                />
                <Popup
                  content="Collapse"
                  trigger={
                    <Button
                      onClick={(_e) => setCollapsed(toggleCollapse)}
                      circular
                      name="collapse"
                      icon="angle double down"
                      color="orange"
                    />
                  }
                />
                <Dropdown icon="filter" floating button className="icon">
                  <Dropdown.Menu>
                    <Dropdown.Header icon="filter" content="Filter by Type" />
                    <Dropdown.Divider />
                    <Dropdown.Menu onClick={(e: React.MouseEvent<HTMLDivElement>) => filterNotes(e)} scrolling>
                      {filterNoteTypes.map((option) => (
                        // Same as in `list.tsx`: React reads `key` off the JSX
                        // attributes before applying the spread.
                        // @ts-expect-error TS2783
                        <Dropdown.Item id={option.value} key={option.value} {...option} />
                      ))}
                    </Dropdown.Menu>
                  </Dropdown.Menu>
                </Dropdown>
                <Popup
                  content="Download PDF"
                  trigger={<Button circular onClick={handleDownloadAllNotes} icon="download" color="green" />}
                />
              </span>
            </div>
            <Divider />
          </>
        )}

        <Comment.Group style={{ minHeight: '300px' }}>
          {!(props.notesResponse.length === 1 && props.notesResponse[0]?.resource?.status === 'entered-in-error') &&
            sortedParentNotes.map((key) => displayNoteThread(key, tableData))}
        </Comment.Group>
      </Segment>
    </div>
  );
}

export default NotesListAll;
